import html2pdf from "html2pdf.js";
import { ExerciseInterface, ObjectiveInterface } from "../../../interfaces/examInterfaces";
import { DIALOGS } from "../../Dialogs/dialogReducer";
import { PRINT_TYPES } from "../Reducers/PrintReducer";
import { MAIN_TYPES } from "../Reducers/MainReducer";
import { getAppDispatch } from "../../../stores/dispatchStore";
import {
  handleClickTab,
  handleCloseDialog,
  handleOpenDialog,
  resetDataPages,
  setActiveTab,
  setSelectedObjectiveId,
} from "./FunctionsMain";

export async function createPagesPrint(
  sortedObjectives: ObjectiveInterface[],
  selectedObjectiveId: string,
  currentObjectiveIndex: number,
  allPages: ExerciseInterface[][][],
  pagesCurrentObjective: ExerciseInterface[][],
  allTotalPoints: number[],
  totalPoints: number
) {
  setShowSolutions(false);
  if (selectedObjectiveId === sortedObjectives[currentObjectiveIndex].id) {
    let updatedAllPages = [...allPages];
    updatedAllPages.push([...pagesCurrentObjective]);
    setAllPages(updatedAllPages);

    let updatedAllTotalPoints = [...allTotalPoints];
    updatedAllTotalPoints.push(totalPoints);
    setAllTotalPoints(updatedAllTotalPoints);

    if (currentObjectiveIndex >= sortedObjectives.length - 1) {
      setIsCreatingAllPages(false);
      setIsCreatingPDF(true);
      return;
    }

    resetDataPages();
    const newIndex = currentObjectiveIndex + 1;
    setCurrentObjectiveIndex(newIndex);
    setSelectedObjectiveId(sortedObjectives[currentObjectiveIndex + 1].id);
  }
}

export async function createPdf(
  examName: string,
  firstObjectiveId: string,
  printSolutions: boolean,
  selectedObjectiveId: string
) {
  const element = document.getElementById("exam-pdf");

  if (!element) {
    console.error("Element not found");
    finishCreatingPdf(firstObjectiveId, selectedObjectiveId);
    return;
  }
  const options = {
    margin: [0, 0, 0, 0],
    filename: "exam.pdf",
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      width: element.clientWidth,
      height: element.clientHeight,
      scale: 1,
      logging: false,
      useCORS: true,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
  };

  await html2pdf()
    .set({ ...options, filename: `${examName}.pdf` })
    .from(element)
    .save();

  if (printSolutions) {
    setShowSolutions(true);
    setIsCreatingPDFSolutions(true);
    return;
  } else {
    finishCreatingPdf(firstObjectiveId, selectedObjectiveId);
  }
}

export async function createPdfSolutions(examName: string, firstObjectiveId: string, selectedObjectiveId: string) {
  const element = document.getElementById("exam-pdf");

  if (!element) {
    console.error("Element not found");
    finishCreatingPdf(firstObjectiveId, selectedObjectiveId);
    return;
  }

  const options = {
    margin: [0, 0, 0, 0],
    filename: "exam.pdf",
    image: { type: "jpeg", quality: 1 },
    html2canvas: {
      width: element.clientWidth,
      height: element.clientHeight,
      scale: 1,
      logging: false,
      useCORS: true,
    },
    jsPDF: {
      unit: "mm",
      format: "a4",
      orientation: "portrait",
    },
  };

  await html2pdf()
    .set({ ...options, filename: `${examName}_Loesungen.pdf` })
    .from(element)
    .save();

  setShowSolutions(false);

  finishCreatingPdf(firstObjectiveId, selectedObjectiveId);
}

export function printExam(
  sortedObjectives: ObjectiveInterface[],
  selectedObjectiveId: string,
  printSolutions: boolean,
  createPageNumbers: boolean
) {
  const firstObjectiveId: string = sortedObjectives[0].id;
  let allObjectives: ObjectiveInterface[] = [];

  handleOpenDialog(DIALOGS.CREATE_PDF_DIALOG);
  handleCloseDialog(DIALOGS.PRINT_EXAM_DIALOG);

  if (selectedObjectiveId !== firstObjectiveId) {
    resetDataPages();
    setSelectedObjectiveId(firstObjectiveId);
  }

  setCreatePageNumbers(createPageNumbers);
  setIsCreatingPDF(false);
  setIsCreatingPDFSolutions(false);
  setPrintSolutions(printSolutions);
  setAllTotalPoints([]);
  setAllPages([]);
  setCurrentObjectiveIndex(0);
  setIsCreatingAllPages(true);

  for (const objective of sortedObjectives) {
    allObjectives.push(objective);
  }

  setAllObjectives(allObjectives);
}

function finishCreatingPdf(firstObjectiveId: string, selectedObjectiveId: string) {
  setIsCreatingPDFSolutions(false);
  setIsCreatingPDF(false);
  handleClickTab(firstObjectiveId, 0, selectedObjectiveId);
  setActiveTab(0);
  handleCloseDialog(DIALOGS.CREATE_PDF_DIALOG);
}

function setAllPages(updatedAllPages: ExerciseInterface[][][]) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.ALL_PAGES, payload: updatedAllPages });
}

function setAllTotalPoints(updatedAllTotalPoints: number[]) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.ALL_TOTAL_POINTS, payload: updatedAllTotalPoints });
}

function setIsCreatingAllPages(isCreatingAllPages: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.IS_CREATING_ALL_PAGES, payload: isCreatingAllPages });
}

export function setIsCreatingPDF(isCreatingPdf: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.IS_CREATING_PDF, payload: isCreatingPdf });
}

function setIsCreatingPDFSolutions(isCreatingPdfSolutions: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.IS_CREATING_PDF_SOLUTIONS, payload: isCreatingPdfSolutions });
}

function setAllObjectives(allObjectives: ObjectiveInterface[]) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.ALL_OBJECTIVES, payload: allObjectives });
}

function setCurrentObjectiveIndex(index: number) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.CURRENT_OBJECTIVE_INDEX, payload: index });
}

function setShowSolutions(showSolutions: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: MAIN_TYPES.SHOW_SOLUTIONS, payload: showSolutions });
}

export function setPrintSolutions(printSolutions: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.PRINT_SOLUTIONS, payload: printSolutions });
}

export function setCreatePageNumbers(createPageNumbers: boolean) {
  const dispatch = getAppDispatch();
  dispatch({ type: PRINT_TYPES.CREATE_PAGE_NUMBERS, payload: createPageNumbers });
}
